import { useQueries, useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { useAuthenticatedClient, queryKeys } from '@/api/queries'
import { POLL_NORMAL } from '@/api/polling'
import { formatApiErrorMessage } from '@/lib/api-error'
import type { components } from '@/api/schema'
import type { PaginatedParams, ProjectPriority } from '@/api/hooks'
import type { ServiceTier } from '@/lib/service-tier'
import type { ReplyAttachmentDraft } from '@/features/communications/types'
import type { EmailRecipients, EmailRecipientsOverride } from '@/components/use-email-recipients'
import { useActionItemSource, type ActionItemSource } from '@/features/purchase-orders/action-source'
import { manufacturerDataKeys } from '@/features/manufacturer-data/query-keys'
import { invalidateWarrantyClaims } from '@/features/warranty-claims/api'
import { useAuth } from '@/hooks/use-auth'

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:80'

interface PurchaseOrderParams extends PaginatedParams {
  status?: string
  stage?: string
  internal_order_id?: number
  customer_group_id?: number
  manufacturer_uuid?: string
  ops_person_id?: number
  project_priority?: ProjectPriority
  service_tier?: ServiceTier[]
}

/** Downloads a PO's PDF, for the PO tables' download buttons. */
export function useDownloadPoPdf() {
  const { getAccessToken } = useAuth()
  return async (uuid: string, filename?: string) => {
    const token = await getAccessToken()
    if (!token) return
    const response = await fetch(`${API_BASE_URL}/dashboard/purchase-orders/${uuid}/pdf`, {
      headers: { Authorization: `Bearer ${token}` },
    })
    if (!response.ok) return
    const blob = await response.blob()
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = filename || `PO-${uuid}.pdf`
    a.click()
    URL.revokeObjectURL(url)
  }
}

export function usePurchaseOrders(params?: PurchaseOrderParams) {
  const getClient = useAuthenticatedClient()
  return useQuery({
    queryKey: queryKeys.purchaseOrders.list(params),
    queryFn: async () => {
      const client = await getClient()
      const { data, error } = await client.GET('/dashboard/purchase-orders', {
        params: { query: params },
      })
      if (error) throw error
      return data
    },
    refetchInterval: POLL_NORMAL,
  })
}

export function usePurchaseOrder(uuid: string) {
  const getClient = useAuthenticatedClient()
  return useQuery({
    queryKey: queryKeys.purchaseOrders.detail(uuid),
    queryFn: async () => {
      const client = await getClient()
      const { data, error } = await client.GET('/dashboard/purchase-orders/{uuid}', {
        params: { path: { uuid } },
      })
      if (error) throw error
      return data
    },
    enabled: !!uuid,
    refetchInterval: POLL_NORMAL,
  })
}

export function usePurchaseOrderDetails(uuids: string[]) {
  const getClient = useAuthenticatedClient()
  return useQueries({
    queries: [...new Set(uuids)].map((uuid) => ({
      queryKey: queryKeys.purchaseOrders.detail(uuid),
      queryFn: async () => {
        const client = await getClient()
        const { data, error } = await client.GET('/dashboard/purchase-orders/{uuid}', {
          params: { path: { uuid } },
        })
        if (error) throw error
        return data
      },
      enabled: !!uuid,
      refetchInterval: POLL_NORMAL,
    })),
  })
}

/** Ops's open items on the invoices linked to this PO, each worked on its invoice (USA-1641). */
export function usePurchaseOrderInvoiceActions(uuid: string) {
  const getClient = useAuthenticatedClient()
  return useQuery({
    queryKey: queryKeys.purchaseOrders.invoiceActions(uuid),
    queryFn: async () => {
      const client = await getClient()
      const { data, error } = await client.GET('/dashboard/purchase-orders/{uuid}/invoice-actions', {
        params: { path: { uuid } },
      })
      if (error) throw error
      return data
    },
    enabled: !!uuid,
    refetchInterval: POLL_NORMAL,
  })
}

export function usePurchaseOrderAutomationHistory(uuid: string) {
  const getClient = useAuthenticatedClient()
  return useQuery({
    queryKey: queryKeys.purchaseOrders.automationHistory(uuid),
    queryFn: async () => {
      const client = await getClient()
      const { data, error } = await client.GET('/dashboard/purchase-orders/{uuid}/automation-history', {
        params: { path: { uuid } },
      })
      if (error) throw error
      return data
    },
    enabled: !!uuid,
  })
}

/** Who turned automatic freight booking on or off for the PO, newest first (USA-1738). */
export function usePurchaseOrderFreightBookingHistory(uuid: string) {
  const getClient = useAuthenticatedClient()
  return useQuery({
    queryKey: queryKeys.purchaseOrders.freightBookingHistory(uuid),
    queryFn: async () => {
      const client = await getClient()
      const { data, error } = await client.GET('/dashboard/purchase-orders/{uuid}/freight-booking-history', {
        params: { path: { uuid } },
      })
      if (error) throw error
      return data
    },
    enabled: !!uuid,
  })
}

/**
 * Live carrier options for a shipment we book: price, transit and delivery date per carrier (USA-1738).
 * Asks the carrier each time, so it only runs once staff open the comparison.
 */
export function useShipmentCarrierOptions(poUuid: string, shipmentId: number, enabled: boolean) {
  const getClient = useAuthenticatedClient()
  return useQuery({
    queryKey: queryKeys.purchaseOrders.carrierOptions(poUuid, shipmentId),
    queryFn: async () => {
      const client = await getClient()
      const { data, error } = await client.GET(
        '/dashboard/purchase-orders/{uuid}/shipments/{shipment_id}/carrier-options',
        { params: { path: { uuid: poUuid, shipment_id: shipmentId } } },
      )
      if (error) throw error
      return data
    },
    enabled,
    // Carrier offers expire; a comparison older than a few minutes is asked again.
    staleTime: 5 * 60 * 1000,
    retry: false,
  })
}

export type CarrierOption = components['schemas']['ShipmentCarrierOptionRead']

/** Book a shipment with the carrier option staff chose from its live options (USA-1738). */
export function useBookCarrierOption(poUuid: string) {
  const getClient = useAuthenticatedClient()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({ shipmentId, option }: { shipmentId: number; option: CarrierOption }) => {
      const client = await getClient()
      const { data, error } = await client.POST(
        '/dashboard/purchase-orders/{uuid}/shipments/{shipment_id}/book',
        {
          params: { path: { uuid: poUuid, shipment_id: shipmentId } },
          body: {
            option: {
              carrier_name: option.carrier_name,
              service_level: option.service_level,
              scac_code: option.scac_code,
              provider_service_code: option.provider_service_code,
            },
          },
        },
      )
      if (error) throw error
      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.purchaseOrders.detail(poUuid) })
      queryClient.invalidateQueries({ queryKey: queryKeys.shipments.all })
    },
  })
}

/** The PO's planned and expected ship and arrival against the customer's date (USA-1329). */
export function usePurchaseOrderDeliveryTimeline(uuid: string) {
  const getClient = useAuthenticatedClient()
  return useQuery({
    queryKey: queryKeys.purchaseOrders.deliveryTimeline(uuid),
    queryFn: async () => {
      const client = await getClient()
      const { data, error } = await client.GET('/dashboard/purchase-orders/{uuid}/delivery-timeline', {
        params: { path: { uuid } },
      })
      if (error) throw error
      return data ?? null
    },
    enabled: !!uuid,
  })
}

export function useUpdatePurchaseOrder(uuid: string) {
  const getClient = useAuthenticatedClient()
  const queryClient = useQueryClient()
  const detailKey = queryKeys.purchaseOrders.detail(uuid)
  return useMutation({
    mutationFn: async (body: {
      automation_disabled?: boolean
      // Stop booking this PO's freight automatically; staff choose the carrier (USA-1738).
      freight_auto_booking_disabled?: boolean
      // Which of the manufacturer's sources the PO is bought through. Saving re-generates the PDF.
      sourcing_option_id?: number
      we_book_freight?: boolean
      // Who pays freight the manufacturer books (USA-1658). Saving re-generates the PDF.
      freight_billing?: 'USA-RS UPS account' | 'Prepay & add' | 'Manufacturer charges freight' | 'Manufacturer'
      // null clears back to "auto" (server falls back to weight-based routing)
      freight_mode?: 'parcel' | 'ltl' | null
      // Free-text PO instructions; '' clears them. Saving re-generates the PO PDF.
      instructions?: string | null
      shipping_instructions?: string | null
      ship_to_name?: string | null
      ship_to_company?: string | null
      ship_to_address1?: string | null
      ship_to_address2?: string | null
      ship_to_city?: string | null
      ship_to_state?: string | null
      ship_to_zip?: string | null
      ship_to_country?: string | null
      ship_to_phone?: string | null
      ship_to_override_reason?: string | null
      ship_to_expected_revision?: number | null
      // Change it even though a shipment is booked; Operations then updates the carrier.
      ship_to_override_after_booking?: boolean
      // Delivery requirements; saving rebuilds the shipping instructions and PDF.
      accessorials?: components['schemas']['PurchaseOrderAccessorialsUpdate']
    }) => {
      const client = await getClient()
      const { data, error } = await client.PATCH('/dashboard/purchase-orders/{uuid}', {
        params: { path: { uuid } },
        body,
      })
      if (error) throw error
      return data
    },
    onMutate: async (body) => {
      await queryClient.cancelQueries({ queryKey: detailKey })
      const previous = queryClient.getQueryData(detailKey)
      queryClient.setQueryData(detailKey, (old: Record<string, unknown> | undefined) => {
        if (!old) return old
        const { accessorials, sourcing_option_id, ...rest } = body
        // The source lives on its own record; the picker reads the chosen id from it.
        const fields =
          sourcing_option_id === undefined
            ? rest
            : { ...rest, source: { ...(old.source as Record<string, unknown>), sourcing_option_id } }
        if (!accessorials) return { ...old, ...fields }
        const snapshot = { ...(old.dropship_accessorial_snapshot as Record<string, unknown>) }
        for (const [key, value] of Object.entries(accessorials)) snapshot[key] = { value }
        const site =
          accessorials.residential_delivery == null
            ? {}
            : { residential_delivery: accessorials.residential_delivery }
        return { ...old, ...fields, ...site, dropship_accessorial_snapshot: snapshot }
      })
      return { previous }
    },
    onError: (_err, _vars, context) => {
      if (context?.previous) queryClient.setQueryData(detailKey, context.previous)
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.purchaseOrders.all })
      queryClient.invalidateQueries({ queryKey: detailKey })
    },
  })
}

export function useUpdateSubtotalDiscount(uuid: string) {
  const getClient = useAuthenticatedClient()
  const queryClient = useQueryClient()
  const detailKey = queryKeys.purchaseOrders.detail(uuid)
  return useMutation({
    // A null discount_type removes the discount. Saving re-generates the PO PDF.
    mutationFn: async (body: components['schemas']['PurchaseOrderSubtotalDiscountUpdate']) => {
      const client = await getClient()
      const { data, error } = await client.PUT('/dashboard/purchase-orders/{uuid}/subtotal-discount', {
        params: { path: { uuid } },
        body,
      })
      if (error) throw error
      return data
    },
    // Show the server's new totals right away; the full PO refetch follows.
    onSuccess: (po) => {
      queryClient.setQueryData(detailKey, (old: Record<string, unknown> | undefined) =>
        old
          ? {
              ...old,
              total: po.total,
              subtotal_discount: po.subtotal_discount ?? null,
            }
          : old,
      )
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.purchaseOrders.all })
      queryClient.invalidateQueries({ queryKey: detailKey })
    },
  })
}

export function useHoldPurchaseOrder(uuid: string) {
  const getClient = useAuthenticatedClient()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (body: { reason: string; note?: string | null }) => {
      const client = await getClient()
      const { data, error } = await client.POST('/dashboard/purchase-orders/{uuid}/hold', {
        params: { path: { uuid } },
        body,
      })
      if (error) throw error
      return data
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.purchaseOrders.all })
      queryClient.invalidateQueries({ queryKey: queryKeys.purchaseOrders.detail(uuid) })
      queryClient.invalidateQueries({ queryKey: queryKeys.apInvoices.all })
    },
  })
}

export function useReleasePurchaseOrderHold(uuid: string) {
  const getClient = useAuthenticatedClient()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (body: { reason: string }) => {
      const client = await getClient()
      const { data, error } = await client.POST(
        '/dashboard/purchase-orders/{uuid}/release-hold',
        {
          params: { path: { uuid } },
          body,
        },
      )
      if (error) throw error
      return data
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.purchaseOrders.all })
      queryClient.invalidateQueries({ queryKey: queryKeys.purchaseOrders.detail(uuid) })
      queryClient.invalidateQueries({ queryKey: queryKeys.apInvoices.all })
    },
  })
}

export function useUpdateInvoiceExpectation(uuid: string) {
  const getClient = useAuthenticatedClient()
  const queryClient = useQueryClient()
  const detailKey = queryKeys.purchaseOrders.detail(uuid)
  return useMutation({
    mutationFn: async ({ action, reason }: { action: 'waive' | 'cancel'; reason: string }) => {
      const client = await getClient()
      const request =
        action === 'waive'
          ? client.POST('/dashboard/purchase-orders/{uuid}/invoice-expectation/waive', {
              params: { path: { uuid } },
              body: { reason },
            })
          : client.POST('/dashboard/purchase-orders/{uuid}/invoice-expectation/cancel', {
              params: { path: { uuid } },
              body: { reason },
            })
      const { data, error } = await request
      if (error) throw error
      return data
    },
    onMutate: async ({ action, reason }) => {
      await queryClient.cancelQueries({ queryKey: detailKey })
      const previous = queryClient.getQueryData(detailKey)
      const now = new Date().toISOString()
      queryClient.setQueryData<components['schemas']['PurchaseOrderDetailRead']>(detailKey, (old) => {
        if (!old?.invoice_expectation) return old
        return {
          ...old,
          invoice_expectation: {
            ...old.invoice_expectation,
            status: action === 'waive' ? 'waived' : 'cancelled',
            updated_at: now,
            ...(action === 'waive'
              ? { waived_at: now, waiver_reason: reason }
              : { cancelled_at: now, cancellation_reason: reason }),
          },
        }
      })
      return { previous }
    },
    onError: (_error, _variables, context) => {
      if (context?.previous !== undefined) queryClient.setQueryData(detailKey, context.previous)
    },
    onSuccess: (expectation) => {
      queryClient.setQueryData<components['schemas']['PurchaseOrderDetailRead']>(detailKey, (old) =>
        old ? { ...old, invoice_expectation: expectation } : old,
      )
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: detailKey })
      queryClient.invalidateQueries({ queryKey: queryKeys.purchaseOrders.all })
    },
  })
}

export function useUpdateLineItem(poUuid: string) {
  const getClient = useAuthenticatedClient()
  const queryClient = useQueryClient()
  const detailKey = queryKeys.purchaseOrders.detail(poUuid)
  return useMutation({
    mutationFn: async ({
      lineItemId,
      discountChain,
      unitPrice,
      quantity,
      listPrice,
      model,
      description,
    }: {
      lineItemId: number
      discountChain?: string
      unitPrice?: string
      quantity?: number
      listPrice?: string
      model?: string
      description?: string
    }) => {
      const client = await getClient()
      const { data, error } = await client.PATCH(
        '/dashboard/purchase-orders/{uuid}/line-items/{line_item_id}',
        {
          params: { path: { uuid: poUuid, line_item_id: lineItemId } },
          body: {
            discount_chain: discountChain,
            unit_price: unitPrice,
            quantity: quantity,
            list_price: listPrice,
            model,
            description,
          },
        },
      )
      if (error) throw error
      return data
    },
    onSuccess: (lineItem) => {
      if (!lineItem) return
      queryClient.setQueryData<components['schemas']['PurchaseOrderDetailRead']>(
        detailKey,
        (current) =>
          current
            ? {
                ...current,
                line_items: current.line_items.map((item) =>
                  item.id === lineItem.id ? lineItem : item,
                ),
              }
            : current,
      )
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: detailKey })
    },
  })
}

export function useAddLineItem(poUuid: string) {
  const getClient = useAuthenticatedClient()
  const queryClient = useQueryClient()
  const detailKey = queryKeys.purchaseOrders.detail(poUuid)
  return useMutation({
    mutationFn: async (body: {
      product_id?: number | null
      product_uuid?: string | null
      model?: string | null
      description?: string | null
      selling_unit?: string | null
      quantity: number
      list_price?: string | null
      net_price?: string | null
      discount_display?: string | null
      unit_price?: string | null
    }) => {
      const client = await getClient()
      const { data, error } = await client.POST(
        '/dashboard/purchase-orders/{uuid}/line-items',
        {
          params: { path: { uuid: poUuid } },
          body,
        },
      )
      if (error) throw error
      return data
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: detailKey })
    },
  })
}

export function useDeleteLineItem(poUuid: string) {
  const getClient = useAuthenticatedClient()
  const queryClient = useQueryClient()
  const detailKey = queryKeys.purchaseOrders.detail(poUuid)
  return useMutation({
    mutationFn: async ({ lineItemId }: { lineItemId: number }) => {
      const client = await getClient()
      const { data, error } = await client.DELETE(
        '/dashboard/purchase-orders/{uuid}/line-items/{line_item_id}',
        {
          params: { path: { uuid: poUuid, line_item_id: lineItemId } },
        },
      )
      if (error) throw error
      return data
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: detailKey })
    },
  })
}

/**
 * The cached record whose `actions` list holds the action items on screen: the PO, the customer thread,
 * the manufacturer's data agent (USA-1474), or a warranty claim's items (USA-915).
 */
function actionsDetailKey(source: ActionItemSource, poUuid: string): readonly unknown[] {
  if (source.kind === 'customer_thread') return queryKeys.customerComms.detail(source.threadId)
  if (source.kind === 'manufacturer_data') return manufacturerDataKeys.agent(source.manufacturerUuid)
  if (source.kind === 'warranty_claim') return source.actionsKey
  return queryKeys.purchaseOrders.detail(poUuid)
}

type WarrantySource = Extract<ActionItemSource, { kind: 'warranty_claim' }>

/** A warranty item's routes take its UUID; the cards pass its numeric id. */
function warrantyActionUuid(source: WarrantySource, actionId: number): string {
  const uuid = source.actionUuids[actionId]
  if (!uuid) throw new Error('This warranty item is no longer listed here. Reload the page and try again.')
  return uuid
}

/** Refreshes the Action Items lists an item off a PO counts in, after it changes. */
function invalidateSourceActionLists(queryClient: ReturnType<typeof useQueryClient>, source: ActionItemSource) {
  invalidateActionLists(queryClient)
  if (source.kind === 'manufacturer_data') {
    queryClient.invalidateQueries({ queryKey: manufacturerDataKeys.actionsAll })
    queryClient.invalidateQueries({ queryKey: manufacturerDataKeys.threads(source.manufacturerUuid) })
  }
  // A warranty item lists on its claim (PO page, customer Claims tab); a reply lands on the PO's warranty thread.
  if (source.kind === 'warranty_claim') {
    invalidateWarrantyClaims(queryClient)
    if (source.poUuid) queryClient.invalidateQueries({ queryKey: queryKeys.communications.byPO(source.poUuid) })
  }
}

export function useResolveAction(poUuid: string) {
  const getClient = useAuthenticatedClient()
  const queryClient = useQueryClient()
  const source = useActionItemSource()
  const detailKey = actionsDetailKey(source, poUuid)
  return useMutation({
    mutationFn: async ({ actionId, status, notes }: { actionId: number; status: string; notes?: string }) => {
      const client = await getClient()
      if (source.kind === 'customer_thread') {
        const { data, error } = await client.PATCH('/dashboard/customer-communications/actions/{action_id}', {
          params: { path: { action_id: actionId } },
          body: { status: status as 'resolved' | 'dismissed', notes },
        })
        if (error) throw error
        return data
      }
      if (source.kind === 'manufacturer_data') {
        const { data, error } = await client.PATCH('/dashboard/manufacturer-data-actions/{action_id}', {
          params: { path: { action_id: actionId } },
          body: { status: status as 'resolved' | 'dismissed', notes },
        })
        if (error) throw error
        return data
      }
      if (source.kind === 'warranty_claim') {
        const { data, error } = await client.PATCH('/dashboard/warranty-claims/actions/{action_uuid}', {
          params: { path: { action_uuid: warrantyActionUuid(source, actionId) } },
          body: { status: status as 'resolved' | 'dismissed', notes },
        })
        if (error) throw error
        return data
      }
      const { data, error } = await client.PATCH('/dashboard/purchase-orders/{uuid}/actions/{action_id}', {
        params: { path: { uuid: poUuid, action_id: actionId } },
        body: { status, notes },
      })
      if (error) throw error
      return data
    },
    onMutate: async ({ actionId, status }) => {
      await queryClient.cancelQueries({ queryKey: detailKey })
      const previous = queryClient.getQueryData(detailKey)
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      queryClient.setQueryData(detailKey, (old: any) =>
        old
          ? {
              ...old,
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              actions: old.actions?.map((a: any) =>
                a.id === actionId ? { ...a, status } : a,
              ),
            }
          : old,
      )
      return { previous }
    },
    onError: (_err, _vars, context) => {
      if (context?.previous) queryClient.setQueryData(detailKey, context.previous)
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: detailKey })
      invalidateSourceActionLists(queryClient, source)
    },
  })
}

export type RefundRequestDraft = components['schemas']['RefundRequestDraftRead']
export type RefundOutcome = components['schemas']['RefundOutcomeCreate']

/** The drafted refund-or-credit request for a cancelled PO we paid for, and who it goes to (USA-1471). */
export function useRefundRequestDraft(poUuid: string) {
  const getClient = useAuthenticatedClient()
  return useMutation({
    mutationFn: async ({ actionId }: { actionId: number }) => {
      const client = await getClient()
      const { data, error } = await client.POST('/dashboard/purchase-orders/{uuid}/actions/{action_id}/refund-request/draft', {
        params: { path: { uuid: poUuid, action_id: actionId } },
      })
      if (error) throw error
      return data
    },
  })
}

/** Send the request: the item closes and the refund or credit waits on the PO's Waiting-on list. */
export function useSendRefundRequest(poUuid: string) {
  const getClient = useAuthenticatedClient()
  const queryClient = useQueryClient()
  const source = useActionItemSource()
  const detailKey = actionsDetailKey(source, poUuid)
  return useMutation({
    mutationFn: async ({
      actionId,
      amount,
      bodyText,
      recipients,
    }: {
      actionId: number
      amount: string
      bodyText: string
      recipients?: EmailRecipientsOverride
    }) => {
      const client = await getClient()
      const { data, error } = await client.POST('/dashboard/purchase-orders/{uuid}/actions/{action_id}/refund-request', {
        params: { path: { uuid: poUuid, action_id: actionId } },
        body: { amount, body_text: bodyText, ...recipients },
      })
      if (error) throw error
      return data
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: detailKey })
      queryClient.invalidateQueries({ queryKey: ['owed-items'] })
      invalidateSourceActionLists(queryClient, source)
    },
  })
}

/** The supplier already gave the money back: as a refund, or a credit on our account. */
export function useRecordRefundOutcome(poUuid: string) {
  const getClient = useAuthenticatedClient()
  const queryClient = useQueryClient()
  const source = useActionItemSource()
  const detailKey = actionsDetailKey(source, poUuid)
  return useMutation({
    mutationFn: async ({ actionId, ...body }: RefundOutcome & { actionId: number }) => {
      const client = await getClient()
      const { data, error } = await client.POST('/dashboard/purchase-orders/{uuid}/actions/{action_id}/refund-outcome', {
        params: { path: { uuid: poUuid, action_id: actionId } },
        body,
      })
      if (error) throw error
      return data
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: detailKey })
      invalidateSourceActionLists(queryClient, source)
    },
  })
}

/** An item that closes or reopens also leaves or rejoins the Action Items page (and its queue) and the home summary. */
function invalidateActionLists(queryClient: ReturnType<typeof useQueryClient>) {
  queryClient.invalidateQueries({ queryKey: queryKeys.projects.all })
  queryClient.invalidateQueries({ queryKey: queryKeys.dashboardSummary.all })
}

/**
 * Mark an open action item In Progress, or back to not started (USA-1443). The card shows the change at
 * once; the Action Items lists refetch so their status filter sees it.
 */
export function useSetActionProgress(poUuid: string) {
  const getClient = useAuthenticatedClient()
  const queryClient = useQueryClient()
  const source = useActionItemSource()
  const detailKey = actionsDetailKey(source, poUuid)
  return useMutation({
    mutationFn: async ({ actionId, inProgress }: { actionId: number; inProgress: boolean; userId?: string }) => {
      const client = await getClient()
      const body = { in_progress: inProgress }
      if (source.kind === 'customer_thread') {
        const { data, error } = await client.POST('/dashboard/customer-communications/actions/{action_id}/progress', {
          params: { path: { action_id: actionId } },
          body,
        })
        if (error) throw error
        return data
      }
      if (source.kind === 'manufacturer_data') {
        const { data, error } = await client.POST('/dashboard/manufacturer-data-actions/{action_id}/progress', {
          params: { path: { action_id: actionId } },
          body,
        })
        if (error) throw error
        return data
      }
      if (source.kind === 'warranty_claim') {
        const { data, error } = await client.POST('/dashboard/warranty-claims/actions/{action_uuid}/progress', {
          params: { path: { action_uuid: warrantyActionUuid(source, actionId) } },
          body,
        })
        if (error) throw error
        return data
      }
      const { data, error } = await client.POST('/dashboard/purchase-orders/{uuid}/actions/{action_id}/progress', {
        params: { path: { uuid: poUuid, action_id: actionId } },
        body,
      })
      if (error) throw error
      return data
    },
    onMutate: async ({ actionId, inProgress, userId }) => {
      await queryClient.cancelQueries({ queryKey: detailKey })
      const previous = queryClient.getQueryData(detailKey)
      const now = new Date().toISOString().replace('Z', '')
      type Progress = { id: number; started_by: string; started_at: string; ended_by?: string | null; ended_at?: string | null }
      const toggle = (progress: Progress[] = []): Progress[] => {
        const current = progress.at(-1)
        if (inProgress) {
          return current && !current.ended_at
            ? progress
            : [...progress, { id: -1, started_by: userId ?? '', started_at: now }]
        }
        return current && !current.ended_at
          ? [...progress.slice(0, -1), { ...current, ended_by: userId ?? null, ended_at: now }]
          : progress
      }
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      queryClient.setQueryData(detailKey, (old: any) =>
        old
          ? {
              ...old,
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              actions: old.actions?.map((a: any) => (a.id === actionId ? { ...a, progress: toggle(a.progress) } : a)),
            }
          : old,
      )
      return { previous }
    },
    onError: (_err, _vars, context) => {
      if (context?.previous) queryClient.setQueryData(detailKey, context.previous)
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: detailKey })
      queryClient.invalidateQueries({ queryKey: queryKeys.projects.all })
      invalidateSourceActionLists(queryClient, source)
    },
  })
}

export function useReopenAction(poUuid: string) {
  const getClient = useAuthenticatedClient()
  const queryClient = useQueryClient()
  const source = useActionItemSource()
  const detailKey = actionsDetailKey(source, poUuid)
  return useMutation({
    mutationFn: async (actionId: number) => {
      const client = await getClient()
      if (source.kind === 'customer_thread') {
        const { data, error } = await client.POST('/dashboard/customer-communications/actions/{action_id}/reopen', {
          params: { path: { action_id: actionId } },
        })
        if (error) throw error
        return data
      }
      if (source.kind === 'manufacturer_data') {
        const { data, error } = await client.POST('/dashboard/manufacturer-data-actions/{action_id}/reopen', {
          params: { path: { action_id: actionId } },
        })
        if (error) throw error
        return data
      }
      if (source.kind === 'warranty_claim') {
        const { data, error } = await client.POST('/dashboard/warranty-claims/actions/{action_uuid}/reopen', {
          params: { path: { action_uuid: warrantyActionUuid(source, actionId) } },
        })
        if (error) throw error
        return data
      }
      const { data, error } = await client.POST('/dashboard/purchase-orders/{uuid}/actions/{action_id}/reopen', {
        params: { path: { uuid: poUuid, action_id: actionId } },
      })
      if (error) throw error
      return data
    },
    onMutate: async (actionId) => {
      await queryClient.cancelQueries({ queryKey: detailKey })
      const previous = queryClient.getQueryData(detailKey)
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      queryClient.setQueryData(detailKey, (old: any) =>
        old
          ? {
              ...old,
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              actions: old.actions?.map((a: any) => (a.id === actionId ? { ...a, status: 'open' } : a)),
            }
          : old,
      )
      return { previous }
    },
    onError: (_err, _vars, context) => {
      if (context?.previous) queryClient.setQueryData(detailKey, context.previous)
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: detailKey })
      invalidateSourceActionLists(queryClient, source)
    },
  })
}

export type ExpediteCallTerms = 'net' | 'prepay' | 'card' | 'unknown'

/**
 * Close a "Call to expedite" item with what the supplier said (USA-1192). The call goes on the PO's
 * conversation, where the email AI records the ship date and terms.
 */
export function useRecordExpediteCall(poUuid: string) {
  const getClient = useAuthenticatedClient()
  const queryClient = useQueryClient()
  const source = useActionItemSource()
  const detailKey = queryKeys.purchaseOrders.detail(poUuid)
  return useMutation({
    mutationFn: async ({
      actionId,
      shipDate,
      terms,
      notes,
    }: {
      actionId: number
      shipDate: string | null
      terms: ExpediteCallTerms | null
      notes: string
    }) => {
      const client = await getClient()
      const { data, error } = await client.POST(
        '/dashboard/purchase-orders/{uuid}/actions/{action_id}/record-expedite-call',
        {
          params: { path: { uuid: poUuid, action_id: actionId } },
          body: { ship_date: shipDate, terms, notes: notes || null },
        },
      )
      if (error) throw error
      return data
    },
    onMutate: async ({ actionId }) => {
      await queryClient.cancelQueries({ queryKey: detailKey })
      const previous = queryClient.getQueryData(detailKey)
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      queryClient.setQueryData(detailKey, (old: any) =>
        old
          ? {
              ...old,
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              actions: old.actions?.map((action: any) =>
                action.id === actionId ? { ...action, status: 'resolved' } : action,
              ),
            }
          : old,
      )
      return { previous }
    },
    // The item closes on the page at once, which unmounts the form: say how it went from here.
    onSuccess: () => {
      toast.success('Call recorded')
    },
    onError: (error, _variables, context) => {
      if (context?.previous) queryClient.setQueryData(detailKey, context.previous)
      toast.error(formatApiErrorMessage('Call not recorded', error))
    },
    onSettled: () => {
      // The call lands on the PO's conversation.
      queryClient.invalidateQueries({ queryKey: queryKeys.purchaseOrders.all })
      invalidateSourceActionLists(queryClient, source)
    },
  })
}

/** Close a "No tracking number" item with the number staff found; it goes on the item's shipment (USA-1554). */
export function useRecordTracking(poUuid: string) {
  const getClient = useAuthenticatedClient()
  const queryClient = useQueryClient()
  const detailKey = queryKeys.purchaseOrders.detail(poUuid)
  return useMutation({
    mutationFn: async ({ actionId, trackingNumber }: { actionId: number; trackingNumber: string }) => {
      const client = await getClient()
      const { data, error } = await client.POST(
        '/dashboard/purchase-orders/{uuid}/actions/{action_id}/record-tracking',
        {
          params: { path: { uuid: poUuid, action_id: actionId } },
          body: { tracking_number: trackingNumber },
        },
      )
      if (error) throw error
      return data
    },
    onMutate: async ({ actionId }) => {
      await queryClient.cancelQueries({ queryKey: detailKey })
      const previous = queryClient.getQueryData(detailKey)
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      queryClient.setQueryData(detailKey, (old: any) =>
        old
          ? {
              ...old,
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              actions: old.actions?.map((action: any) =>
                action.id === actionId ? { ...action, status: 'resolved' } : action,
              ),
            }
          : old,
      )
      return { previous }
    },
    onError: (_error, _variables, context) => {
      if (context?.previous) queryClient.setQueryData(detailKey, context.previous)
    },
    onSettled: () => {
      // The shipment, its status and the PO's status can all change.
      queryClient.invalidateQueries({ queryKey: queryKeys.purchaseOrders.all })
    },
  })
}

export function useRecordManufacturerOrder(poUuid: string) {
  const getClient = useAuthenticatedClient()
  const queryClient = useQueryClient()
  const detailKey = queryKeys.purchaseOrders.detail(poUuid)
  return useMutation({
    mutationFn: async ({ actionId, orderNumber }: { actionId: number; orderNumber: string }) => {
      const client = await getClient()
      const { data, error } = await client.POST(
        '/dashboard/purchase-orders/{uuid}/actions/{action_id}/record-manufacturer-order',
        {
          params: { path: { uuid: poUuid, action_id: actionId } },
          body: { manufacturer_order_number: orderNumber },
        },
      )
      if (error) throw error
      return data
    },
    onMutate: async ({ actionId, orderNumber }) => {
      await queryClient.cancelQueries({ queryKey: detailKey })
      const previous = queryClient.getQueryData(detailKey)
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      queryClient.setQueryData(detailKey, (old: any) =>
        old
          ? {
              ...old,
              manufacturer_order_number: orderNumber,
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              actions: old.actions?.map((action: any) =>
                action.id === actionId ? { ...action, status: 'resolved' } : action,
              ),
            }
          : old,
      )
      return { previous }
    },
    onError: (_error, _variables, context) => {
      if (context?.previous) queryClient.setQueryData(detailKey, context.previous)
    },
    onSettled: () => {
      // The PO status can change too, so list views refresh as well.
      queryClient.invalidateQueries({ queryKey: queryKeys.purchaseOrders.all })
    },
  })
}

/** Give the manufacturer its PO email; this PO and its other waiting POs then send. */
export function useAddPoEmail(poUuid: string) {
  const getClient = useAuthenticatedClient()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({ actionId, email }: { actionId: number; email: string }) => {
      const client = await getClient()
      const { data, error } = await client.POST(
        '/dashboard/purchase-orders/{uuid}/actions/{action_id}/add-po-email',
        {
          params: { path: { uuid: poUuid, action_id: actionId } },
          body: { email },
        },
      )
      if (error) throw error
      return data
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.purchaseOrders.all })
      queryClient.invalidateQueries({ queryKey: queryKeys.manufacturers.all })
    },
  })
}

export function useResolveDeliverySite(poUuid: string) {
  const getClient = useAuthenticatedClient()
  const queryClient = useQueryClient()
  const detailKey = queryKeys.purchaseOrders.detail(poUuid)
  return useMutation({
    mutationFn: async ({ actionId, residentialDelivery }: { actionId: number; residentialDelivery: boolean }) => {
      const client = await getClient()
      const { data, error } = await client.POST(
        '/dashboard/purchase-orders/{uuid}/actions/{action_id}/resolve-delivery-site',
        {
          params: { path: { uuid: poUuid, action_id: actionId } },
          body: { residential_delivery: residentialDelivery },
        },
      )
      if (error) throw error
      return data
    },
    onMutate: async ({ actionId }) => {
      await queryClient.cancelQueries({ queryKey: detailKey })
      const previous = queryClient.getQueryData(detailKey)
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      queryClient.setQueryData(detailKey, (old: any) =>
        old
          ? {
              ...old,
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              actions: old.actions?.map((action: any) =>
                action.id === actionId ? { ...action, status: 'resolved' } : action,
              ),
            }
          : old,
      )
      return { previous }
    },
    onError: (_error, _variables, context) => {
      if (context?.previous) queryClient.setQueryData(detailKey, context.previous)
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: detailKey })
    },
  })
}

export function useCreateAction(poUuid: string) {
  const getClient = useAuthenticatedClient()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (body: {
      action_type: string
      summary: string
      details?: string | null
      assigned_role: string | null
      line_item_id?: number | null
      expected_date?: string | null
      charge_amount?: number | null
    }) => {
      const client = await getClient()
      const { data, error } = await client.POST('/dashboard/purchase-orders/{uuid}/actions', {
        params: { path: { uuid: poUuid } },
        body,
      })
      if (error) throw error
      return data
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.purchaseOrders.detail(poUuid) })
    },
  })
}

export function useRemoveActionItem(poUuid: string) {
  const getClient = useAuthenticatedClient()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({
      actionId,
      lineItemId,
      notes,
    }: {
      actionId: number
      lineItemId?: number
      notes?: string
    }) => {
      const client = await getClient()
      const { data, error } = await client.POST(
        '/dashboard/purchase-orders/{uuid}/actions/{action_id}/remove-item',
        {
          params: { path: { uuid: poUuid, action_id: actionId } },
          body: { line_item_id: lineItemId, notes },
        },
      )
      if (error) throw error
      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.purchaseOrders.detail(poUuid) })
    },
  })
}

export function useReplaceActionItem(poUuid: string) {
  const getClient = useAuthenticatedClient()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({
      actionId,
      lineItemId,
      replacementProductId,
      replacementUnitPrice,
      notes,
    }: {
      actionId: number
      lineItemId?: number
      replacementProductId: number
      replacementUnitPrice?: number
      notes?: string
    }) => {
      const client = await getClient()
      const { data, error } = await client.POST(
        '/dashboard/purchase-orders/{uuid}/actions/{action_id}/replace-item',
        {
          params: { path: { uuid: poUuid, action_id: actionId } },
          body: {
            line_item_id: lineItemId,
            replacement_product_id: replacementProductId,
            replacement_unit_price: replacementUnitPrice,
            notes,
          },
        },
      )
      if (error) throw error
      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.purchaseOrders.detail(poUuid) })
    },
  })
}

export function useApplyDiscountChainToAll(poUuid: string) {
  const getClient = useAuthenticatedClient()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({ discountChain }: { discountChain: string }) => {
      const client = await getClient()
      const { data, error } = await client.POST(
        '/dashboard/purchase-orders/{uuid}/apply-discount-chain',
        {
          params: { path: { uuid: poUuid } },
          body: { discount_chain: discountChain },
        },
      )
      if (error) throw error
      return data
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.purchaseOrders.detail(poUuid) })
    },
  })
}

export function useUpdateDiscountAction(poUuid: string) {
  const getClient = useAuthenticatedClient()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({
      actionId,
      newDiscountChain,
      notes,
    }: {
      actionId: number
      newDiscountChain: string
      notes?: string
    }) => {
      const client = await getClient()
      const { data, error } = await client.POST(
        '/dashboard/purchase-orders/{uuid}/actions/{action_id}/update-discount',
        {
          params: { path: { uuid: poUuid, action_id: actionId } },
          body: { new_discount_chain: newDiscountChain, notes },
        },
      )
      if (error) throw error
      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.purchaseOrders.detail(poUuid) })
    },
  })
}

export function useReplacementCandidates(poUuid: string, actionId: number, search: string) {
  const getClient = useAuthenticatedClient()
  return useQuery({
    queryKey: ['replacementCandidates', poUuid, actionId, search],
    queryFn: async () => {
      const client = await getClient()
      const { data, error } = await client.GET(
        '/dashboard/purchase-orders/{uuid}/actions/{action_id}/replacement-candidates',
        {
          params: { path: { uuid: poUuid, action_id: actionId }, query: { search } },
        },
      )
      if (error) throw error
      return data
    },
    enabled: search.length >= 1,
  })
}

export function useAcceptFee(poUuid: string) {
  const getClient = useAuthenticatedClient()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({
      actionId,
      feeDescription,
      feeAmount,
      notes,
    }: {
      actionId: number
      feeDescription: string
      feeAmount: number
      notes?: string
    }) => {
      const client = await getClient()
      const { data, error } = await client.POST(
        '/dashboard/purchase-orders/{uuid}/actions/{action_id}/accept-fee',
        {
          params: { path: { uuid: poUuid, action_id: actionId } },
          body: { fee_description: feeDescription, fee_amount: feeAmount, notes },
        },
      )
      if (error) throw error
      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.purchaseOrders.detail(poUuid) })
    },
  })
}

export function useReviseLineItemAction(poUuid: string) {
  const getClient = useAuthenticatedClient()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({
      actionId,
      lineItemId,
      quantity,
      unitPrice,
      discountChain,
    }: {
      actionId: number
      lineItemId?: number
      quantity?: number
      unitPrice?: number
      discountChain?: string
    }) => {
      const client = await getClient()
      const { data, error } = await client.POST(
        '/dashboard/purchase-orders/{uuid}/actions/{action_id}/revise-line-item',
        {
          params: { path: { uuid: poUuid, action_id: actionId } },
          body: {
            line_item_id: lineItemId,
            quantity,
            unit_price: unitPrice,
            discount_chain: discountChain,
          },
        },
      )
      if (error) throw error
      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.purchaseOrders.detail(poUuid) })
    },
  })
}

export function useRecordCancellationAction(poUuid: string) {
  const getClient = useAuthenticatedClient()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({ actionId }: { actionId: number }) => {
      const client = await getClient()
      const { data, error } = await client.POST(
        '/dashboard/purchase-orders/{uuid}/actions/{action_id}/record-cancellation',
        {
          params: { path: { uuid: poUuid, action_id: actionId } },
          body: {},
        },
      )
      if (error) throw error
      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.purchaseOrders.detail(poUuid) })
    },
  })
}

export function useAcceptFreightChargeAction(poUuid: string) {
  const getClient = useAuthenticatedClient()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({ actionId, freightAmount }: { actionId: number; freightAmount: number }) => {
      const client = await getClient()
      const { data, error } = await client.POST(
        '/dashboard/purchase-orders/{uuid}/actions/{action_id}/accept-freight-charge',
        {
          params: { path: { uuid: poUuid, action_id: actionId } },
          body: { freight_amount: freightAmount },
        },
      )
      if (error) throw error
      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.purchaseOrders.detail(poUuid) })
    },
  })
}

/** Ship LTL or small parcel on freight we book, closing a freight mode action (USA-1401). */
export function useResolveFreightModeAction(poUuid: string) {
  const getClient = useAuthenticatedClient()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({ actionId, freightMode }: { actionId: number; freightMode: 'parcel' | 'ltl' }) => {
      const client = await getClient()
      const { data, error } = await client.POST(
        '/dashboard/purchase-orders/{uuid}/actions/{action_id}/freight-mode',
        {
          params: { path: { uuid: poUuid, action_id: actionId } },
          body: { freight_mode: freightMode },
        },
      )
      if (error) throw error
      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.purchaseOrders.detail(poUuid) })
    },
  })
}

/** Buy the PO through the source a supplier named and place it there, closing the action (USA-1506). */
export function useChangeSourceAction(poUuid: string) {
  const getClient = useAuthenticatedClient()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({ actionId, sourcingOptionId }: { actionId: number; sourcingOptionId: number }) => {
      const client = await getClient()
      const { data, error } = await client.POST(
        '/dashboard/purchase-orders/{uuid}/actions/{action_id}/change-source',
        {
          params: { path: { uuid: poUuid, action_id: actionId } },
          body: { sourcing_option_id: sourcingOptionId },
        },
      )
      if (error) throw error
      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.purchaseOrders.detail(poUuid) })
    },
  })
}

/**
 * Move a PO the supplier already has to another source (USA-1689): a new PO goes there and this one
 * is marked Replaced. Toasts here, not per call: the page moves to the new PO.
 */
export function useSwitchPurchaseOrderSource(poUuid: string) {
  const getClient = useAuthenticatedClient()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (body: components['schemas']['PurchaseOrderSwitchSource']) => {
      const client = await getClient()
      const { data, error } = await client.POST('/dashboard/purchase-orders/{uuid}/switch-source', {
        params: { path: { uuid: poUuid } },
        body,
      })
      if (error) throw error
      return data
    },
    onSuccess: (data) => {
      const number = data.purchase_order.po_number
      toast.success(
        data.placement === 'email' ? `PO ${number} created and sent` : `PO ${number} created; place it on the supplier's site`,
      )
      // Without an email thread the old PO's cancellation is an Ops item.
      if (!data.cancellation_emailed) toast.info('Cancel the old PO with its supplier: Ops has an item for it')
    },
    onError: (error) => toast.error(formatApiErrorMessage('Supplier not switched', error)),
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.purchaseOrders.all })
      queryClient.invalidateQueries({ queryKey: queryKeys.purchaseOrders.detail(poUuid) })
    },
  })
}

export function useApproveBackorder(poUuid: string) {
  const getClient = useAuthenticatedClient()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({ actionId, notes }: { actionId: number; notes?: string }) => {
      const client = await getClient()
      const { data, error } = await client.POST(
        '/dashboard/purchase-orders/{uuid}/actions/{action_id}/approve-backorder',
        {
          params: { path: { uuid: poUuid, action_id: actionId } },
          body: { notes },
        },
      )
      if (error) throw error
      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.purchaseOrders.detail(poUuid) })
    },
  })
}

export function useAddDeliveryServices(poUuid: string) {
  const getClient = useAuthenticatedClient()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({ actionId }: { actionId: number }) => {
      const client = await getClient()
      const { data, error } = await client.POST(
        '/dashboard/purchase-orders/{uuid}/actions/{action_id}/add-delivery-services',
        {
          params: { path: { uuid: poUuid, action_id: actionId } },
          body: {},
        },
      )
      if (error) throw error
      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.purchaseOrders.detail(poUuid) })
    },
  })
}

export function useSendCustomerNotification(poUuid: string) {
  const getClient = useAuthenticatedClient()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({
      actionId,
      subject,
      bodyText,
      recipients,
    }: {
      actionId: number
      subject: string
      bodyText: string
      recipients?: EmailRecipientsOverride
    }) => {
      const client = await getClient()
      const { data, error } = await client.POST(
        '/dashboard/purchase-orders/{uuid}/actions/{action_id}/send-customer-notification',
        {
          params: { path: { uuid: poUuid, action_id: actionId } },
          body: { subject, body_text: bodyText, ...recipients },
        },
      )
      if (error) throw error
      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.purchaseOrders.detail(poUuid) })
    },
  })
}

export function useDraftCustomerEmail(poUuid: string) {
  const getClient = useAuthenticatedClient()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({ actionId, context }: { actionId: number; context?: string }) => {
      const client = await getClient()
      const { data, error } = await client.POST(
        '/dashboard/purchase-orders/{uuid}/actions/{action_id}/draft-customer-email',
        {
          params: { path: { uuid: poUuid, action_id: actionId } },
          body: { context: context ?? null },
        },
      )
      if (error) throw error
      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.purchaseOrders.detail(poUuid) })
    },
  })
}

export function useSkipCustomerApproval(poUuid: string) {
  const getClient = useAuthenticatedClient()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({ actionId }: { actionId: number }) => {
      const client = await getClient()
      const { data, error } = await client.POST(
        '/dashboard/purchase-orders/{uuid}/actions/{action_id}/skip-customer-approval',
        {
          params: { path: { uuid: poUuid, action_id: actionId } },
        },
      )
      if (error) throw error
      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.purchaseOrders.detail(poUuid) })
    },
  })
}

/**
 * Who a reply from this action item goes to unless staff change it (USA-1663), from the conversation the
 * reply goes on: the PO's, the customer's or the manufacturer data thread.
 */
export function useActionReplyRecipients(poUuid: string, actionId: number, enabled: boolean) {
  const getClient = useAuthenticatedClient()
  const source = useActionItemSource()
  return useQuery({
    queryKey: ['action-reply-recipients', source.kind, poUuid, actionId],
    enabled,
    staleTime: 30_000,
    queryFn: async (): Promise<EmailRecipients> => {
      const client = await getClient()
      const result =
        source.kind === 'manufacturer_data'
          ? await client.GET('/dashboard/manufacturer-data-actions/{action_id}/reply-recipients', {
              params: { path: { action_id: actionId } },
            })
          : source.kind === 'customer_thread'
            ? await client.GET('/dashboard/customer-communications/actions/{action_id}/reply-recipients', {
                params: { path: { action_id: actionId } },
              })
            : await client.GET('/dashboard/purchase-orders/{uuid}/actions/{action_id}/reply-recipients', {
                params: { path: { uuid: poUuid, action_id: actionId } },
              })
      if (result.error) throw result.error
      return { to: result.data.to_emails ?? [], cc: result.data.cc_emails ?? [] }
    },
  })
}

/** Sales picks Refund $X on a cancelled Quote Builder PO: it goes back through Stripe and the item closes (USA-1693). */
export function useRefundCustomer(poUuid: string) {
  const getClient = useAuthenticatedClient()
  const queryClient = useQueryClient()
  const source = useActionItemSource()
  return useMutation({
    mutationFn: async ({ actionId }: { actionId: number }) => {
      const client = await getClient()
      const { data, error } = await client.POST(
        '/dashboard/purchase-orders/{uuid}/actions/{action_id}/refund-customer',
        { params: { path: { uuid: poUuid, action_id: actionId } } },
      )
      if (error) throw error
      return data
    },
    onSuccess: (item) => {
      if (item?.status === 'resolved') toast.success('Customer refunded')
      else toast.warning('Part refunded. The rest is still on the item.')
      queryClient.invalidateQueries({ queryKey: actionsDetailKey(source, poUuid) })
      invalidateSourceActionLists(queryClient, source)
    },
    onError: (error) => {
      toast.error(formatApiErrorMessage('Refund not made', error))
    },
  })
}

export function useSendDraftResponse(poUuid: string) {
  const getClient = useAuthenticatedClient()
  const queryClient = useQueryClient()
  const source = useActionItemSource()
  return useMutation({
    mutationFn: async ({
      actionId,
      bodyText,
      resolutionType,
      attachments,
      recipientEmail,
      recipientName,
      recipients,
    }: {
      actionId: number
      bodyText: string
      resolutionType?: string
      attachments?: ReplyAttachmentDraft[]
      recipientEmail?: string
      recipientName?: string
      /** Who staff chose to send this email to (USA-1663); omitted keeps who it goes to anyway. */
      recipients?: EmailRecipientsOverride
    }) => {
      const client = await getClient()
      // A manufacturer data item's choice is carried out, its reply (if any) sent, and the item closed.
      if (source.kind === 'manufacturer_data') {
        const { data, error } = await client.POST('/dashboard/manufacturer-data-actions/{action_id}/choose', {
          params: { path: { action_id: actionId } },
          body: { option_type: resolutionType ?? 'custom_reply', body_text: bodyText || null, ...recipients },
        })
        if (error) throw error
        return data
      }
      // A warranty item's choice works the same way, its reply (if any) going from warranty@ (USA-915).
      if (source.kind === 'warranty_claim') {
        const { data, error } = await client.POST('/dashboard/warranty-claims/actions/{action_uuid}/choose', {
          params: { path: { action_uuid: warrantyActionUuid(source, actionId) } },
          body: { option_type: resolutionType ?? 'custom_reply', body_text: bodyText || null },
        })
        if (error) throw error
        return data
      }
      if (source.kind === 'customer_thread') {
        const { data, error } = await client.POST(
          '/dashboard/customer-communications/actions/{action_id}/send-draft-response',
          {
            params: { path: { action_id: actionId } },
            body: {
              body_text: bodyText,
              resolution_type: resolutionType,
              ...recipients,
              attachments: (attachments ?? []).map((attachment) => ({
                filename: attachment.filename,
                content_type: attachment.contentType,
                content_base64: attachment.contentBase64,
              })),
            },
          },
        )
        if (error) throw error
        return data
      }
      const { data, error } = await client.POST(
        '/dashboard/purchase-orders/{uuid}/actions/{action_id}/send-draft-response',
        {
          params: { path: { uuid: poUuid, action_id: actionId } },
          body: {
            body_text: bodyText,
            resolution_type: resolutionType,
            ...(recipientEmail
              ? { recipient_email: recipientEmail, recipient_name: recipientName }
              : {}),
            ...recipients,
            attachments: attachments?.map((attachment) => ({
              filename: attachment.filename,
              content_type: attachment.contentType,
              content_base64: attachment.contentBase64,
            })),
          },
        },
      )
      if (error) throw error
      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: actionsDetailKey(source, poUuid) })
      invalidateSourceActionLists(queryClient, source)
    },
  })
}

export function useSkipSpecCredit(poUuid: string) {
  const getClient = useAuthenticatedClient()
  const queryClient = useQueryClient()
  const detailKey = queryKeys.purchaseOrders.detail(poUuid)
  return useMutation({
    mutationFn: async () => {
      const client = await getClient()
      const { data, error } = await client.POST('/dashboard/purchase-orders/{uuid}/skip-spec-credit', {
        params: { path: { uuid: poUuid } },
      })
      if (error) throw error
      return data
    },
    onMutate: async () => {
      await queryClient.cancelQueries({ queryKey: detailKey })
      const previous = queryClient.getQueryData<components['schemas']['PurchaseOrderDetailRead']>(detailKey)
      queryClient.setQueryData<components['schemas']['PurchaseOrderDetailRead']>(detailKey, (current) =>
        current ? {
          ...current,
          actions: current.actions?.map((action) =>
            action.action_type === 'spec_credit_no_response' && action.status === 'open'
              ? { ...action, status: 'resolved', resolution_type: 'spec_credit_skipped' }
              : action,
          ),
        } : current,
      )
      return { previous }
    },
    onError: (_error, _variables, context) => {
      if (context?.previous) queryClient.setQueryData(detailKey, context.previous)
    },
    onSettled: () => Promise.all([
      queryClient.invalidateQueries({ queryKey: queryKeys.purchaseOrders.all }),
      queryClient.invalidateQueries({ queryKey: queryKeys.communications.all }),
      queryClient.invalidateQueries({ queryKey: queryKeys.dashboardSummary.all }),
    ]),
  })
}

export type ItemStatusChoice = components['schemas']['POLineStatusUpdate']['status']

const ITEM_STATUS_PREVIEW: Record<
  ItemStatusChoice,
  { key: string; label: string; tone: components['schemas']['LineStatusRead']['tone'] } | null
> = {
  backordered: { key: 'backordered', label: 'Backordered', tone: 'warning' },
  shipped: { key: 'shipped', label: 'Shipped', tone: 'progress' },
  delivered: { key: 'delivered', label: 'Delivered', tone: 'success' },
  cancelled: { key: 'cancelled', label: 'Cancelled', tone: 'danger' },
  clear: null,
}

/** Set Backordered, Shipped, Delivered or Cancelled on PO items (USA-1202). */
export function useSetItemStatuses(poUuid: string) {
  const getClient = useAuthenticatedClient()
  const queryClient = useQueryClient()
  const detailKey = queryKeys.purchaseOrders.detail(poUuid)
  return useMutation({
    mutationFn: async (body: components['schemas']['POLineStatusUpdate']) => {
      const client = await getClient()
      const { data, error } = await client.POST('/dashboard/purchase-orders/{uuid}/item-statuses', {
        params: { path: { uuid: poUuid } },
        body,
      })
      if (error) throw error
      return data
    },
    onMutate: async (body) => {
      await queryClient.cancelQueries({ queryKey: detailKey })
      const previous =
        queryClient.getQueryData<components['schemas']['PurchaseOrderDetailRead']>(detailKey)
      const preview = ITEM_STATUS_PREVIEW[body.status]
      if (preview && previous?.status_summary) {
        const chosen = new Set(body.line_item_ids)
        queryClient.setQueryData<components['schemas']['PurchaseOrderDetailRead']>(detailKey, {
          ...previous,
          status_summary: {
            ...previous.status_summary,
            items: (previous.status_summary.items ?? []).map((item) =>
              chosen.has(item.line_item_id)
                ? { ...item, ...preview, source: 'manual', until_date: body.until_date ?? null }
                : item,
            ),
          },
        })
      }
      return { previous }
    },
    onError: (_error, _body, context) => {
      if (context?.previous) queryClient.setQueryData(detailKey, context.previous)
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: detailKey })
      queryClient.invalidateQueries({ queryKey: queryKeys.projects.all })
      queryClient.invalidateQueries({ queryKey: ['purchase-orders', 'list'] })
    },
  })
}

/** Every change to a shipment's dates, carrier and tracking, newest first (USA-1424). */
export function useShipmentHistory(poUuid: string, shipmentId: number) {
  const getClient = useAuthenticatedClient()
  return useQuery({
    // Under the PO detail key, so whatever refreshes the PO refreshes its shipment history.
    queryKey: [...queryKeys.purchaseOrders.detail(poUuid), 'shipment-history', shipmentId] as const,
    queryFn: async () => {
      const client = await getClient()
      const { data, error } = await client.GET(
        '/dashboard/purchase-orders/{uuid}/shipments/{shipment_id}/history',
        { params: { path: { uuid: poUuid, shipment_id: shipmentId } } },
      )
      if (error) throw error
      return data
    },
    refetchInterval: POLL_NORMAL,
  })
}
